import remove from 'assets/remove.svg';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BuildVersionList, toNumber } from 'utils/helper';
import { getItem } from 'utils/localStorageHelper';
import { useConfig } from './ConfigProvider';

interface ToastContextType {
  addToast: (params: ToastType) => void;
  startSse: () => void;
  closeSse: () => void;
}
interface ToastsProviderType {
  children: React.ReactNode;
}

export interface ToastType {
  icon?: string;
  title?: string;
  message: string | React.ReactNode;
  duration?: number; // defultValue = 3000
  color?:
    | ''
    | 'bg-green-200'
    | 'bg-rose-200'
    | 'bg-toast-error-50'
    | 'bg-toast-info-50'
    | 'bg-toast-success-50'
    | 'bg-toast-warning-50'
    | 'bg-toast-primary-200'
    | 'bg-cyfi-error'
    | 'bg-cyfi-info'
    | 'bg-cyfi-success'
    | 'bg-cyfi-warning'
    | 'bg-cyfi-primary'; // defultValue = ''
  text?: 'text-toast-error-800' | 'text-toast-info-800' | 'text-toast-success-800' | 'text-toast-warning-800' | 'text-cyfi-btn';
  svg?: 'toastIcon-warning' | 'toastIcon-success' | 'toastIcon-error' | 'toastIcon-info' | 'cyfi-svg';
  closeFn?: VoidFunction;
  svgCssType?: string;
  toastCssType?: string;
  textCssType?: string;
}

export const Toast = ({
  title,
  message,
  duration,
  color,
  icon,
  text,
  svg,
  closeFn,
  svgCssType = 'w-6 h-6 mr-4',
  toastCssType = 'flex w-full max-w-md p-2 text-gray-500 rounded shadow mb-4',
  textCssType = 'text-xs font-normal',
}: ToastType) => {
  const [show, setShow] = useState<boolean>(true);
  const timeout = useRef<NodeJS.Timeout>();

  useEffect(() => () => clearTimeout(timeout.current), []);

  useEffect(() => {
    if (show === true && duration) {
      setAutohide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, duration]);

  const close = () => {
    clearTimeout(timeout.current);
    setShow(false);
    if (closeFn) {
      closeFn();
    }
  };

  const setAutohide = () => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setShow(false);
    }, duration);
  };

  return (
    <>
      {show && (
        <div className={`${toastCssType} ${color}`}>
          <div className="pt-1 mr-4">
            {icon && (
              <img className={`${svgCssType} ${svg}`} src={icon} alt="" onError={(e) => ((e.target as HTMLTextAreaElement).style.display = 'none')} />
            )}
          </div>
          <div className={`flex-col items-start flex justify-center ${text}`}>
            {title && <p className="text-sm font-semibold">{title}</p>}
            <p className={`${textCssType}`}>{message}</p>
          </div>
          <button className="btn btn-ghost ml-auto hover:bg-transparent" onClick={close}>
            <img src={remove} className={`w-4 h-4 ${svg}`} alt="close" />
          </button>
        </div>
      )}
    </>
  );
};

const ToastContext = React.createContext<ToastContextType>(null!);

export const ToastProvider = ({ children }: ToastsProviderType) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [toasts, setToasts] = useState<ToastType[]>([]);
  const eventSourceRef = useRef<EventSource>();
  const { cbtApiUrl, buildVersion } = useConfig();

  const addToast = ({ title, message, duration = 3000, color = '', icon, text, svg }: ToastType) => {
    setToasts((prevToasts) => [...prevToasts, { title, message, duration, color, icon, text, svg }]);
  };

  const startSse = () => {
    closeSse();
    const eventSource = new EventSource(`${cbtApiUrl}api/sse`);
    eventSource.onmessage = ({ data }) => {
      /* without dispaly notification in rtty page */
      if (/^(\/sites\/\d+)?\/rtty\//.test(location.pathname)) return;

      let { i18nToastSseKey, message, toastColor, siteId, ...params } = JSON.parse(data);

      if (
        buildVersion !== BuildVersionList.portal ||
        (buildVersion === BuildVersionList.portal && toNumber(siteId) === toNumber(getItem('siteId')))
      ) {
        const toastProps: ToastType = { message, color: toastColor ?? 'bg-cyfi-info' };

        if (i18nToastSseKey) {
          const translationKey = `toast.sse.${i18nToastSseKey}`;
          if (i18n.exists(translationKey)) {
            toastProps.message = t(translationKey, params) as React.ReactNode;
          }
          if (i18nToastSseKey === 'rontoServiceUpgradeTimeout') {
            toastProps.duration = 10000;
          }
        }

        if (message) addToast(toastProps);
      }
    };
    eventSourceRef.current = eventSource;
  };

  const closeSse = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
  };

  return (
    <ToastContext.Provider value={{ addToast, startSse, closeSse }}>
      {children}
      {/* toast z-index 需要比 modal z-index:999 高 */}
      <div className="toast toast-center flex items-center w-full max-w-xs" style={{ maxWidth: '33vw', zIndex: 1000 }}>
        {toasts.map((toast, index) => {
          return (
            <Toast
              key={index}
              duration={toast.duration}
              color={toast.color}
              title={toast.title}
              message={toast.message}
              icon={toast.icon}
              text={toast.text}
              svg={toast.svg}
            ></Toast>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => React.useContext(ToastContext);
